import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Backdrop,
    CircularProgress,
    Button,
    Divider,
} from '@mui/material';
import {useLazyQuery, useMutation} from "@apollo/client";
import moment from 'moment';

import {
    GET_DETAILED_SHIFT_PLANS,
    ENROLL_PERFORMER_FROM_SHIFT_PLANS,
    UNENROLL_PERFORMER_FROM_SHIFT_PLANS,
    GENERATE_SHIFTS,
    DELETE_SHIFT_PLANS,
    UPDATE_SHIFTS
} from "../../queries/GraphQLQueries";
import {
    buildTimeblockShiftPlanMap,
    buildPerformerEntriesFromShiftPlans,
    generateTimeBlocksFromShiftPlans,
    generateEmptyGrid,
    buildCurrentEnrollments,
    isUnableToUnenrollUser,
    buildUnenrolledPerformerIds,
    buildEnrolledPerformerIds,
    isShiftPlanAfterCurrentTime,
    SHIFT_PLAN_ERROR_MESSAGE,
    SHIFT_PLAN_SUCCESS_MESSAGE
} from "../../helpers/ShiftPlanHelper";
import {hasSelectedTimeblocks, BOOKABLE_STATUS, hasNewlySelectedTimeblocks} from "../../helpers/TimeblockHelper";
import {SINGLE_DAY_DATE_FORMAT} from "../../config/DateTimeConstants";
import ShiftPlanManagementDialogHeader from "./ShiftPlanManagementDialogHeader";
import ShiftPlanTimeblockGrid from './ShiftPlanTimeblockGrid';
import AddShiftPlanDialog from './AddShiftPlanDialog';
import PerformerSelect from './PerformerSelect';
import CustomAlert from '../CustomAlert';
import BookableStatusSelect from './BookableStatusSelect';

const timeBlockWidthRem = 7;
const visibleBlocks = 6;

const styles = {
    dialog: {
        container: {
            width: '58.5vw',
            minWidth: '32rem',
            position: 'fixed', 
            top: '0', 
            maxHeight: '90vh', 
            display: 'flex', 
            flexDirection: 'column'
        },
        title: {
            paddingLeft: '0.375rem',
            paddingY: '0.5rem',
        },
        content: {
            overflow: 'hidden',
            padding: '0 0.5vw 0 0.5vw',
            display: 'flex',
            flexDirection: 'column'
        },
        actions: {
            container: {
                justifyContent: 'space-between', 
                alignItems: 'end', 
                paddingTop: '0.2rem'
            },
            buttonGroup: {
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
            }
        }
    },
    timeblock: {
        gridDimensions: {
            width: `${timeBlockWidthRem + (timeBlockWidthRem * visibleBlocks)}rem`,
            maxWidth: '100%'
        },
        emptyState: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '100%',
            minHeight: '12.5rem',
            border: '0.0625rem solid #ccc',
            width: `${timeBlockWidthRem + (timeBlockWidthRem * visibleBlocks)}rem`,
            maxWidth: '100%'
        }
    },
    performerSelect: {
        container: {
            margin: '0', 
            position: 'sticky', 
            bottom: 0, 
            background: 'white', 
            zIndex: 1000
        }
    },
    bookableStatus: {
        selectBox: {
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '0.5rem',
        }
    },
    loadingBackdrop: {
        color: '#fff',
        display: 'flex',
        zIndex: 1500
    }
};

export default function ShiftPlanDialog({open, onClose, selectedDate, shouldRefreshShiftPlans, showTimeblocks}) {

    const [visibleTimeBlocks, setVisibleTimeBlocks] = useState([]);
    const [performerEnrollments, setPerformerEnrollments] = useState([]);
    const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
    const [isProcessingShiftPlanData, setIsProcessingShiftPlanData] = useState(false);
    const [currentDate, setCurrentDate] = useState('');
    const [addShiftPlanDialogOpen, setAddShiftPlanDialogOpen] = useState(false);
    const [shiftPlans, setShiftPlans] = useState([]);
    const [shiftPlanAdded, setShiftPlanAdded] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [bookableStatuses, setBookableStatuses] = useState({});
    const [isGeneratingShifts, setIsGeneratingShifts] = useState(false);
    const [isUpdatingShifts, setIsUpdatingShifts] = useState(false);

    const [deleteShiftPlanButtonEnabled, setDeleteShiftPlanButtonEnabled] = useState(false);

    const handleCloseCustomAlert = useCallback(() => setAlertMessage(null), []);

    const [fetchShiftPlans, {loading: isFetchingShiftPlans, error, data}] = useLazyQuery(GET_DETAILED_SHIFT_PLANS, {
        fetchPolicy: 'network-only'
    });

    const refetchDetailedShiftPlans = {
        refetchQueries: [{
            query: GET_DETAILED_SHIFT_PLANS,
            variables: {
                startTime: moment(currentDate).startOf('day').toISOString(),
                endTime: moment(currentDate).endOf('day').toISOString()
            }
        }]
    };

    const [enrollPerformer, { loading: isEnrolling }] = useMutation(ENROLL_PERFORMER_FROM_SHIFT_PLANS, refetchDetailedShiftPlans);
    const [unenrollPerformer, { loading: isUnenrolling }] = useMutation(UNENROLL_PERFORMER_FROM_SHIFT_PLANS, refetchDetailedShiftPlans);
    const [generateShifts] = useMutation(GENERATE_SHIFTS, refetchDetailedShiftPlans);
    const [updateShifts] = useMutation(UPDATE_SHIFTS, refetchDetailedShiftPlans);
    const [deleteShiftPlans] = useMutation(DELETE_SHIFT_PLANS, refetchDetailedShiftPlans);

    useEffect(() => {
        if (!selectedDate?.start) {
            return;
        }
        
        setCurrentDate(selectedDate.start);
    }, [selectedDate?.start]);

    useEffect(() => {
        if (!open || !currentDate) {
            return;
        }
        
        setIsProcessingShiftPlanData(true);
        fetchShiftPlans({
            variables: {
                startTime: moment(currentDate).startOf('day').toISOString(),
                endTime: moment(currentDate).endOf('day').toISOString()
            }
        });
    }, [open, currentDate, fetchShiftPlans]);

    useEffect(() => {
        updateDeleteShiftPlanButtonStatus();
    }, [shiftPlans, currentDate, shiftPlanAdded]);

    useEffect(() => {
        if (!data?.shiftPlans) {
            setShiftPlans([]);
            setPerformerEnrollments([]);
            setVisibleTimeBlocks([]);
            setIsProcessingShiftPlanData(false);
            return;
        }
        
        setShiftPlans(data.shiftPlans);
        
        const flattenedShiftPlans = buildTimeblockShiftPlanMap(data.shiftPlans);
        
        const sortedPerformers = buildPerformerEntriesFromShiftPlans(flattenedShiftPlans, currentDate);
        const allServiceTypes = flattenedShiftPlans.flatMap(plan => 
            (plan.availableServiceTypes || []).map(serviceType => ({
                ...serviceType,
                shiftPlanId: plan.shiftPlanId
            }))
        );

        setAvailableServiceTypes(allServiceTypes);
        setPerformerEnrollments(sortedPerformers);

        const generatedTimeBlocks = generateTimeBlocksFromShiftPlans(flattenedShiftPlans, currentDate);
        setVisibleTimeBlocks(generatedTimeBlocks);
        
        setIsProcessingShiftPlanData(false);
    }, [data, currentDate]);

    useEffect(() => {
        if (shiftPlanAdded && open && currentDate) {
            setIsProcessingShiftPlanData(true);
            fetchShiftPlans({
                variables: {
                    startTime: moment(currentDate).startOf('day').toISOString(),
                    endTime: moment(currentDate).endOf('day').toISOString()
                }
            });
            setShiftPlanAdded(false);
        }
    }, [shiftPlanAdded, open, currentDate, fetchShiftPlans]);

    const openAddShiftPlanDialog = () => setAddShiftPlanDialogOpen(true);

    const handleShiftPlanAdded = () => {
        setShiftPlanAdded(true);
        setAddShiftPlanDialogOpen(false);
        shouldRefreshShiftPlans();
    };

    const doShiftPlansHaveGeneratedShifts = (shiftPlans) => {
        if (shiftPlans.length === 0) {
            return false;
        }
        
        return shiftPlans.some(shiftPlan => {
            if (!shiftPlan?.enrollments?.length) {
                return false;
            }
        
            return shiftPlan.enrollments.some(enrollment =>
                enrollment?.availability !== null
            );
        });        
    };

    const updateDeleteShiftPlanButtonStatus = () => {
        const hasShiftPlansAvailable = shiftPlans.length > 0;
        const hasNoGeneratedShifts = !doShiftPlansHaveGeneratedShifts(shiftPlans);
        const isAfterCurrentTime = isShiftPlanAfterCurrentTime(shiftPlans[0]?.startTime);
        
        const canEnableDeleteButton = hasShiftPlansAvailable && hasNoGeneratedShifts && isAfterCurrentTime;
        
        setDeleteShiftPlanButtonEnabled(canEnableDeleteButton);
    };

    const handleDeleteShiftPlans = () => {
        setIsProcessingShiftPlanData(true);
        const selectedIds = shiftPlans.map(plan => plan.id);

        deleteShiftPlans({variables: {shiftPlanIds: selectedIds}})
        .then(() => {         
            shouldRefreshShiftPlans();
        })
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log("error " + error)
        })
    };

    const moveToNextDay = () => {
        const nextDay = new Date(currentDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setCurrentDate(nextDay);
    };

    const moveToPreviousDay = () => {
        const previousDay = new Date(currentDate);
        previousDay.setDate(previousDay.getDate() - 1);
        setCurrentDate(previousDay);
    };

    const hasShiftPlans = () => {
        return shiftPlans && shiftPlans.length > 0;
    };

    const hasEnrolledPerformers = () => {
        return performerEnrollments && performerEnrollments.length > 0;
    };

    const handleError = (error) => {
        setAlertMessage({ children: error?.message, severity: 'error' });
        console.log('error ' + error);
    };

    const handleUnenrollPerformers = (performerIds, shiftPlanId) => {
        performerIds.forEach(performerId => {
            unenrollPerformer({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId
                }
            })
            .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.PERFORMER_UNENROLLED, severity: 'success'}))
            .catch(handleError);
        });
    };

    const handleEnrollPerformers = (performerIds, shiftPlanId) => {
        performerIds.forEach(performerId => {
            enrollPerformer({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId
                }
            })
            .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.PERFORMER_ENROLLED, severity: 'success'}))
            .catch(handleError);
        });
    };

    const handlePerformerEnrollment = (event, shiftPlanId) => {
        const selectedPerformerIds = event.target.value;
        const currentEnrollments = buildCurrentEnrollments(shiftPlans, shiftPlanId);

        if (isUnableToUnenrollUser(currentEnrollments, selectedPerformerIds)) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_ALREADY_GENERATED, severity: 'error' });
            return;
        }

        const unenrolledPerformerIds = buildUnenrolledPerformerIds(currentEnrollments, selectedPerformerIds);
        if (unenrolledPerformerIds.length > 0) {
            handleUnenrollPerformers(unenrolledPerformerIds, shiftPlanId);
            return;
        }

        const enrolledPerformerIds = buildEnrolledPerformerIds(currentEnrollments, selectedPerformerIds);
        if (enrolledPerformerIds.length > 0) {
            handleEnrollPerformers(enrolledPerformerIds, shiftPlanId);
        }
    };

    const handleBookableStatusChange = (shiftPlanId, updatedBookableStatuses) => {
        setBookableStatuses(prevData => {
            const updatedData = { ...prevData };
            updatedData[shiftPlanId] = updatedBookableStatuses;
            return updatedData;
        });
    };

    const mapBookableServiceTypesForShiftPlan = () => {
        if (!shiftPlans || shiftPlans.length === 0) {
            return null;
        }

        const firstShiftPlan = shiftPlans[0];
        const bookableStatusList = bookableStatuses[firstShiftPlan.id] ||
            firstShiftPlan.bookableServiceTypePlans?.map(() => BOOKABLE_STATUS.BOOKABLE) || [];

        const mappedBookableServiceTypes = firstShiftPlan.bookableServiceTypePlans?.map((plan, index) => ({
            id: plan.id,
            isBookable: bookableStatusList[index] === BOOKABLE_STATUS.BOOKABLE
        })) || [];

        return {
            shiftPlanId: firstShiftPlan.id,
            bookableServiceTypes: mappedBookableServiceTypes
        };
    };

    const handleGenerateShifts = () => {
        if (isGeneratingShifts) {
            return;
        }

        const shiftData = mapBookableServiceTypesForShiftPlan();
        if (!shiftData) {
            return;
        }

        setIsGeneratingShifts(true);

        generateShifts({
            variables: { shiftPlans: [shiftData] }
        })
        .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.SHIFTS_GENERATED, severity: 'success'}))
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.error('Error generating shifts:', error);
        })
        .finally(() => setIsGeneratingShifts(false));
    };

    const handleUpdateShifts = () => {
        if (isUpdatingShifts) {
            return;
        }

        const shiftData = mapBookableServiceTypesForShiftPlan();
        if (!shiftData) {
            return;
        }

        setIsUpdatingShifts(true);

        updateShifts({
            variables: { shiftPlans: [shiftData] }
        })
            .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.SHIFTS_UPDATED, severity: 'success'}))
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.error('Error updating shifts:', error);
            })
            .finally(() => setIsUpdatingShifts(false));
    };

    const emptyGrid = useMemo(() => generateEmptyGrid(shiftPlans), [shiftPlans]);
    const firstShiftPlan = shiftPlans[0];

    const isDataReady = !isProcessingShiftPlanData && !isFetchingShiftPlans;
    const hasNoShiftPlans = !hasShiftPlans();
    const hasShiftPlansButNoPerformers = hasShiftPlans() && !hasEnrolledPerformers();
    const hasShiftPlansWithPerformers = hasShiftPlans() && hasEnrolledPerformers();
    const shouldShowBookableStatusSelection = hasShiftPlans() && firstShiftPlan.bookableServiceTypePlans && firstShiftPlan.bookableServiceTypePlans.length > 0;
    const displayLoadingBar = isProcessingShiftPlanData || isFetchingShiftPlans || isEnrolling || isUnenrolling || isGeneratingShifts || isUpdatingShifts;
    const isShiftPlanInPast = isShiftPlanAfterCurrentTime(firstShiftPlan?.startTime);

    const canGenerateShifts = hasShiftPlans() && hasEnrolledPerformers() && hasSelectedTimeblocks(shiftPlans);
    const canUpdateShifts = hasNewlySelectedTimeblocks(shiftPlans);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth={false} slotProps={{paper: {sx: styles.dialog.container}}}>
                <DialogTitle sx={styles.dialog.title}>
                    <ShiftPlanManagementDialogHeader
                        currentDate={currentDate ? `${moment(currentDate).format(SINGLE_DAY_DATE_FORMAT)}` : '—'}
                        handlePreviousDay={moveToPreviousDay}
                        handleNextDay={moveToNextDay}
                        handleClose={onClose}
                        showTimeblocks={showTimeblocks}/>
                </DialogTitle>

                <DialogContent sx={styles.dialog.content}>
                    {
                      isDataReady
                      &&
                      <>
                          {
                            hasNoShiftPlans
                            &&
                            (
                              <Box sx={styles.timeblock.emptyState}>
                                  <Typography variant="body1" color="text.secondary">
                                      No shift plans found
                                  </Typography>
                              </Box>
                            )}

                          {
                            hasShiftPlansButNoPerformers
                            &&
                            (
                              <ShiftPlanTimeblockGrid
                                performerEnrollments={emptyGrid.performers}
                                timeblocks={visibleTimeBlocks}
                                isReadOnly={true}
                                selectedDate={currentDate}
                              />
                            )}

                          {
                            hasShiftPlansWithPerformers
                            &&
                            (
                              <ShiftPlanTimeblockGrid
                                performerEnrollments={performerEnrollments}
                                timeblocks={visibleTimeBlocks}
                                availableServiceTypes = {availableServiceTypes}
                                isReadOnly={false}
                                selectedDate={currentDate}
                              />
                            )}
                      </>
                    }
                </DialogContent>

                {
                  isDataReady
                  &&
                  <>
                    {
                        hasShiftPlans()
                        &&
                        (
                          <Box sx={styles.performerSelect.container}>
                            <PerformerSelect
                              shiftPlan={firstShiftPlan}
                              handlePerformerEnrollment={handlePerformerEnrollment}
                              handlePerformerSelection={() => {}}
                              compactView={true} />
                          </Box>
                        )}                        
                  </>
                }

                <Divider/>

                <DialogActions sx={styles.dialog.actions.container}>
                    <Box sx={styles.dialog.actions.buttonGroup}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={openAddShiftPlanDialog}
                            disabled={hasShiftPlans()}>
                            Add
                        </Button>
                        <Button
                            size="small"
                            variant="contained" 
                            onClick={handleDeleteShiftPlans}
                            disabled={!deleteShiftPlanButtonEnabled}>
                            Delete
                        </Button>
                    </Box>

                    {
                      shouldShowBookableStatusSelection
                      && (
                        <Box sx={styles.bookableStatus.selectBox}>
                            <BookableStatusSelect
                              serviceTypePlans={firstShiftPlan.bookableServiceTypePlans}
                              bookableServices={firstShiftPlan.bookableServiceTypePlans.map(plan => plan.serviceType.keycode)}
                              bookableServiceTypes={bookableStatuses[firstShiftPlan.id] || firstShiftPlan.bookableServiceTypePlans.map(() => "Bookable")}
                              setBookableServiceTypes={(updatedList) => handleBookableStatusChange(firstShiftPlan.id, updatedList)}
                            />
                        </Box>
                      )}
                    <Box
                        sx={{...styles.dialog.actions.buttonGroup,
                            flexDirection: {xs: "column", md: "row"}}}>
                        <Box>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleUpdateShifts}
                                disabled={!canUpdateShifts || isUpdatingShifts || !isShiftPlanInPast}>
                                Update
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleGenerateShifts}
                                disabled={!canGenerateShifts || isGeneratingShifts || !isShiftPlanInPast}>
                                Generate
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>

            <Backdrop open={displayLoadingBar} sx={styles.loadingBackdrop}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <AddShiftPlanDialog
                openAddShiftPlanDialog={addShiftPlanDialogOpen}
                handleClose={() => setAddShiftPlanDialogOpen(false)}
                onShiftPlanAdded={handleShiftPlanAdded}
                currentDate={currentDate} />

            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
        </>
    );
}